import { ApplicationRef, Injectable, inject, signal } from '@angular/core';

const LOCALE_STORAGE_KEY = 'app_locale';

const SUPPORTED_LOCALES = ['pt-BR', 'en-US', 'es', 'it', 'fr', 'de', 'zh-CN', 'ja', 'ru'] as const;

export type AppLocale = (typeof SUPPORTED_LOCALES)[number];

export const APP_SUPPORTED_LOCALES: readonly AppLocale[] = SUPPORTED_LOCALES;

@Injectable({ providedIn: 'root' })
export class LocaleService {
  private readonly appRef = inject(ApplicationRef);
  private readonly defaultLocale: AppLocale = 'pt-BR';

  readonly current = signal<AppLocale>(this.readInitialLocale());

  get currentTag(): AppLocale {
    return this.current();
  }

  setLocale(tag: string): void {
    if (!this.isSupported(tag)) {
      return;
    }

    const locale = tag as AppLocale;

    localStorage.setItem(LOCALE_STORAGE_KEY, locale);

    if (this.current() !== locale) {
      this.current.set(locale);
      this.applyDocumentLocale(locale);
      queueMicrotask(() => this.appRef.tick());
    }
  }

  private readInitialLocale(): AppLocale {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);

    if (stored && this.isSupported(stored)) {
      return stored;
    }

    return this.resolveFromBrowser() ?? this.defaultLocale;
  }

  private applyDocumentLocale(locale: AppLocale): void {
    if (typeof document === 'undefined') {
      return;
    }

    document.documentElement.lang = locale;
  }

  private isSupported(tag: string): tag is AppLocale {
    return SUPPORTED_LOCALES.includes(tag as AppLocale);
  }

  private resolveFromBrowser(): AppLocale | null {
    const browserLocale = (navigator.language ?? '').toLowerCase();

    if (browserLocale.startsWith('pt')) {
      return 'pt-BR';
    }

    if (browserLocale.startsWith('en')) {
      return 'en-US';
    }

    if (browserLocale.startsWith('es')) {
      return 'es';
    }

    if (browserLocale.startsWith('it')) {
      return 'it';
    }

    if (browserLocale.startsWith('fr')) {
      return 'fr';
    }

    if (browserLocale.startsWith('de')) {
      return 'de';
    }

    if (browserLocale.startsWith('zh')) {
      return 'zh-CN';
    }

    if (browserLocale.startsWith('ja')) {
      return 'ja';
    }

    if (browserLocale.startsWith('ru')) {
      return 'ru';
    }

    return null;
  }
}
